import React, { Component, ErrorInfo, ReactNode } from 'react';
import { logger } from '../../utils/logger';

interface ErrorBoundaryFallbackProps {
  error?: Error;
  resetError?: () => void;
}

type ErrorBoundaryFallback =
  | ReactNode
  | React.ComponentType<ErrorBoundaryFallbackProps>;

interface Props {
  children: ReactNode;
  fallback?: ErrorBoundaryFallback;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  resetOnPropsChange?: boolean;
  resetKeys?: Array<string | number>;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
  errorId?: string;
}

export class ErrorBoundary extends Component<Props, State> {
  private resetTimeoutId: number | null = null;

  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Update state so the next render will show the fallback UI
    return { 
      hasError: true,
      error,
      errorId: `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error details
    logger.error('ErrorBoundary caught an error', { error, errorInfo }, 'ErrorBoundary');
    
    // Update state with error info
    this.setState({
      error,
      errorInfo
    });

    // Report to error tracking service
    this.reportError(error, errorInfo);

    // Call custom error handler
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  componentDidUpdate(prevProps: Props) {
    const { resetKeys, resetOnPropsChange } = this.props;
    const { hasError } = this.state;

    // Reset error boundary when resetKeys change
    if (hasError && resetKeys && prevProps.resetKeys) {
      const hasResetKeyChanged = resetKeys.some((key, index) => 
        key !== prevProps.resetKeys![index]
      );
      
      if (hasResetKeyChanged) {
        this.resetErrorBoundary();
      }
    }

    // Reset on any prop change if enabled
    if (hasError && resetOnPropsChange && prevProps !== this.props) {
      this.resetErrorBoundary();
    }
  }

  componentWillUnmount() {
    if (this.resetTimeoutId) {
      clearTimeout(this.resetTimeoutId);
    }
  }

  private reportError = async (error: Error, errorInfo: ErrorInfo) => {
    // Report to analytics/tracking service
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', 'exception', {
        description: error.message,
        fatal: false,
        error_id: this.state.errorId
      });
    }

    // Report to logger in development
    if (import.meta.env.DEV) {
      logger.error('Error Boundary Caught Error', { 
        error, 
        errorInfo, 
        componentStack: errorInfo.componentStack,
        errorId: this.state.errorId
      }, 'ErrorBoundary');
    }

    // ✅ STEP 2.4: Sentry error tracking integration
    if (import.meta.env.PROD && import.meta.env.VITE_SENTRY_DSN) {
      try {
        const Sentry = await import('@sentry/react');
        Sentry.captureException(error, {
          tags: { source: 'errorBoundary' },
          contexts: {
            react: {
              componentStack: errorInfo.componentStack,
            }
          },
          extra: {
            errorId: this.state.errorId
          }
        });
      } catch (sentryError) {
        // Ignore Sentry errors - don't break error reporting
        if (import.meta.env.DEV) {
          logger.debug('Sentry captureException failed in ErrorBoundary', { error: sentryError }, 'ErrorBoundary');
        }
      }
    }
  };

  private resetErrorBoundary = () => {
    if (this.resetTimeoutId) {
      clearTimeout(this.resetTimeoutId);
    }

    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  private handleRetry = () => {
    this.resetErrorBoundary();
  };

  private handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        const fallback = this.props.fallback;

        if (React.isValidElement(fallback)) {
          return fallback;
        }

        // Functional component (or React.memo forwardRef returning function)
        if (typeof fallback === 'function') {
          const FallbackComponent = fallback;
          return (
            <FallbackComponent
              error={this.state.error}
              resetError={this.resetErrorBoundary}
            />
          );
        }

        // React.memo / forwardRef returns an object with $$typeof
        if (typeof fallback === 'object' && fallback !== null && '$$typeof' in fallback) {
          return fallback as React.ReactNode;
        }

        return fallback;
      }

      // Default error UI
      return (
        <div className="min-h-screen flex items-center justify-center theme-muted">
          <div className="max-w-md w-full theme-surface shadow-lg rounded-lg p-6 border theme-border">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0">
                <svg className="h-8 w-8 text-red-500 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-medium theme-text">
                  Something went wrong
                </h3>
                <p className="text-sm theme-text-muted">
                  We're sorry, but something unexpected happened.
                </p>
              </div>
            </div>
            
            {import.meta.env.DEV && this.state.error && (
              <div className="mb-4 p-3 bg-red-500/10 dark:bg-red-500/20 border border-red-500/30 dark:border-red-500/40 rounded-md">
                <p className="text-sm text-red-700 dark:text-red-300 font-mono">
                  {this.state.error.message}
                </p>
                {this.state.errorInfo && (
                  <details className="mt-2">
                    <summary className="text-xs text-red-600 dark:text-red-400 cursor-pointer">
                      Component Stack
                    </summary>
                    <pre className="text-xs text-red-600 dark:text-red-400 mt-1 whitespace-pre-wrap">
                      {this.state.errorInfo.componentStack}
                    </pre>
                  </details>
                )}
              </div>
            )}
            
            <div className="flex space-x-3">
              <button
                onClick={this.handleRetry}
                className="flex-1 bg-[var(--ring)] text-white px-4 py-2 rounded-md text-sm font-medium hover:opacity-90 active:opacity-80 focus:outline-none focus:ring-2 focus:ring-[var(--ring)] focus:ring-offset-2 transition-opacity"
              >
                Try Again
              </button>
              <button
                onClick={this.handleReload}
                className="flex-1 theme-muted theme-text border theme-border px-4 py-2 rounded-md text-sm font-medium hover:theme-surface focus:outline-none focus:ring-2 focus:ring-[var(--ring)] focus:ring-offset-2 transition-colors"
              >
                Reload Page
              </button>
            </div>
            
            <div className="mt-4 text-xs theme-text-muted">
              Error ID: {this.state.errorId}
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Higher-order component for easier usage
export const withErrorBoundary = <P extends object>(
  Component: React.ComponentType<P>,
  errorBoundaryProps?: Omit<Props, 'children'>
) => {
  const WrappedComponent = (props: P) => (
    <ErrorBoundary {...errorBoundaryProps}>
      <Component {...props} />
    </ErrorBoundary>
  );
  
  WrappedComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name})`;
  
  return WrappedComponent;
};

// Hook for error boundary context
export const useErrorHandler = () => {
  const [error, setError] = React.useState<Error | null>(null);
  
  const resetError = React.useCallback(() => {
    setError(null);
  }, []);
  
  const captureError = React.useCallback((error: Error) => {
    setError(error);
  }, []);
  
  React.useEffect(() => {
    if (error) {
      throw error;
    }
  }, [error]);
  
  return { captureError, resetError };
};